import { formatFileSize } from '@agentic-ivy/shared-types';
import { AlertCircle, Check, Film, HardDrive, Link2, Loader2, X, Youtube } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FootageLinkKind } from '@/lib/footage/links';
import { useFootageIntakeStore, type FootageItem } from '@/stores/footageIntakeStore';

const LINK_ICON: Record<FootageLinkKind, typeof Film> = {
  youtube: Youtube,
  drive: HardDrive,
  dropbox: HardDrive,
  s3: Link2,
  video: Link2,
};

const LINK_LABEL: Record<FootageLinkKind, string> = {
  youtube: 'YouTube',
  drive: 'Google Drive',
  dropbox: 'Dropbox',
  s3: 'S3 link',
  video: 'Video link',
};

function statusText(item: FootageItem): string | null {
  switch (item.status) {
    case 'uploading':
      return `${Math.floor(item.pct)}%`;
    case 'importing':
      return 'importing';
    case 'done':
      return item.url ? 'queued' : 'uploaded';
    default:
      return null;
  }
}

/** A limit message already names the file and its size; any other failure needs the name beside it. */
function failureText({ name, error }: FootageItem): string {
  if (!error) return `${name}: failed`;
  return error.includes(name) ? error : `${name}: ${error}`;
}

function FootageChip({ item, onRemove }: { item: FootageItem; onRemove: () => void }) {
  const Icon = item.linkKind ? LINK_ICON[item.linkKind] : Film;
  const busy = item.status === 'uploading' || item.status === 'importing';
  const status = statusText(item);
  const failed = item.status === 'error';
  return (
    <li
      className={cn(
        'relative overflow-hidden flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg text-xs',
        failed ? 'max-w-full bg-destructive/10 text-destructive' : 'max-w-[320px] bg-muted text-foreground',
      )}
      title={item.url ? `${LINK_LABEL[item.linkKind!]}: ${item.url}` : item.name}
    >
      {item.status === 'uploading' && (
        <span className="absolute inset-y-0 left-0 bg-primary/15 transition-all" style={{ width: `${item.pct}%` }} />
      )}
      <Icon className="relative w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
      {failed ? (
        <span className="relative min-w-0 whitespace-normal break-words">{failureText(item)}</span>
      ) : (
        <>
          <span className="relative truncate">{item.name}</span>
          {item.size != null && (
            <span className="relative flex-shrink-0 text-[10px] text-muted-foreground tabular-nums">{formatFileSize(item.size)}</span>
          )}
        </>
      )}
      {status && <span className="relative flex-shrink-0 text-[10px] text-muted-foreground tabular-nums">· {status}</span>}
      {busy && <Loader2 className="relative w-3 h-3 animate-spin flex-shrink-0 text-muted-foreground" />}
      {item.status === 'done' && <Check className="relative w-3 h-3 flex-shrink-0 text-emerald-600" />}
      {failed && <AlertCircle className="relative w-3 h-3 flex-shrink-0" />}
      <button
        type="button"
        onClick={onRemove}
        className="relative p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 flex-shrink-0"
        title={busy ? 'Cancel' : 'Remove'}
      >
        <X className="w-3 h-3" />
      </button>
    </li>
  );
}

export function FootageChips({ className }: { className?: string }) {
  const items = useFootageIntakeStore((s) => s.items);
  const remove = useFootageIntakeStore((s) => s.remove);
  if (!items.length) return null;
  const running = items.some((it) => it.status === 'uploading' || it.status === 'importing');
  return (
    <div className={className}>
      <ul className="flex flex-wrap gap-1.5" aria-label="Files">
        {items.map((item) => (
          <FootageChip key={item.id} item={item} onRemove={() => remove(item.id)} />
        ))}
      </ul>
      {running && (
        <p className="mt-1.5 text-[11px] text-muted-foreground">Keeps uploading if you leave this page — just keep the tab open.</p>
      )}
    </div>
  );
}
